import type { Direction, PropGetItemLabel, SelectProps } from 'uikit';
import { Select, Tooltip, useForkRef } from 'uikit';
import { useOnRefAttributeChange } from 'utils/dom';

import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';

type TBaseControlledType<T> = Omit<SelectProps<T>, 'onChange'> &
    UseControllerProps<any> & {
        refTooltip?: React.MutableRefObject<null>;
        clearOnDisabled?: boolean;
        isTooltipHidden?: boolean;
        onTooltipAttributeChange?: (attributeName: string, target: Node) => void;
        classNameTooltip?: string;
        tooltipDirection?: Direction;
        onChange?: (value: T | null) => void;
    };

type IControlledSelectFieldProps<TData> = TData extends {
    label: string;
}
    ? TBaseControlledType<TData>
    : TBaseControlledType<TData> & { getItemLabel: PropGetItemLabel<TData> };

export function ControlledSelectField<T = any>({
    name,
    control,
    rules,
    refTooltip,
    clearOnDisabled,
    isTooltipHidden,
    onTooltipAttributeChange,
    classNameTooltip,
    dropdownClassName,
    tooltipDirection = 'rightCenter',
    onChange: externalOnChange,
    ...props
}: IControlledSelectFieldProps<T>) {
    const ref = useRef(null);
    const { field, fieldState } = useController({
        name,
        control,
        rules,
    });

    const handleOnChange = useCallback(
        ({ value }: { value: T | null }) => {
            field.onChange(value);

            if (externalOnChange) {
                externalOnChange(value);
            }
        },
        [field, externalOnChange],
    );

    const tooltipRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (field.value && props.disabled && clearOnDisabled) {
            field.onChange(null);
        }
    }, [clearOnDisabled, field, props.disabled]);

    useOnRefAttributeChange({
        targetRef: tooltipRef,
        onAttributeChange: onTooltipAttributeChange,
    });

    return (
        <>
            <Select
                value={field.value}
                onChange={handleOnChange}
                labelPosition="left"
                size="s"
                ref={useForkRef([ref, refTooltip])}
                status={fieldState.invalid ? 'alert' : undefined}
                placeholder="Выберите"
                dropdownClassName={dropdownClassName}
                {...(props as any)}
            />
            {!isTooltipHidden && fieldState.invalid && fieldState.error?.message && (
                <Tooltip
                    size="s"
                    direction={tooltipDirection}
                    status="alert"
                    anchorRef={ref}
                    ref={tooltipRef}
                    className={`${classNameTooltip} tooltip-${name}`}
                >
                    {fieldState.error?.message}
                </Tooltip>
            )}
        </>
    );
}

ControlledSelectField.displayName = 'ControlledSelectField';
